import { useSelector } from 'react-redux';
import { getHasModalBeenViewed, Modal } from 'store/modal';

/**
 * Hook to determine if this is the first time a user is viewing a modal.
 * Returns true if the modal has never been viewed before.
 * Returns false if the modal has been viewed at least once (even if replayed).
 *
 * @param modalIdentifier - The modal identifier object with name and props, or just the modal name string
 * @returns boolean - true if first view, false if previously viewed
 *
 * @example
 * // In a slide component
 * const isFirstView = useIsFirstModalView('firstLook');
 * // Use isFirstView to conditionally show/hide buttons or content
 *
 * @example
 * // With props
 * const isFirstView = useIsFirstModalView({ name: 'firstLook', props: { kitId: '123' } });
 */
export const useIsFirstModalView = (
  modalIdentifier: Modal | string,
): boolean => {
  const identifier: Modal =
    typeof modalIdentifier === 'string'
      ? { name: modalIdentifier, props: {} }
      : modalIdentifier;

  const hasBeenViewed = useSelector(getHasModalBeenViewed(identifier));
  return !hasBeenViewed;
};
